'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectIsAuthenticated } from '@repo/store/maps/slices/auth';
import { selectExternalMapId, selectLocationEnabled, selectPlayerCoords, selectPlayerLocationDeltas, selectTrackedCompassPin } from '@repo/store/maps/slices/mapData';
import { gameCoordsToLatLng } from '@repo/types/maps/map-utils';
import type * as MapLibre from 'maplibre-gl';
import { useEffect, useMemo, useRef } from 'react';
import { PIN_INTERACTION_LAYER_IDS } from '../game-map/hooks/constants';
import { useMapContext } from '../game-map/map-context';
const SOURCE_ID = 'compass-route';
const GLOW_LAYER_ID = 'compass-route-glow';
const LINE_LAYER_ID = 'compass-route-line';
const PLAYER_MARKER_PADDING_PX = 18;
const TARGET_MARKER_PADDING_PX = 30;
const MIN_ROUTE_LENGTH_PX = 4;
const EMPTY_ROUTE: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
  type: 'FeatureCollection',
  features: []
};
function insetRouteEndpoints(map: MapLibre.Map, route: GeoJSON.FeatureCollection<GeoJSON.LineString>): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  const feature = route.features[0];
  const start = feature?.geometry.coordinates[0];
  const end = feature?.geometry.coordinates[1];
  if (!feature || !start || !end) return EMPTY_ROUTE;
  const [startLng, startLat] = start;
  const [endLng, endLat] = end;
  if (typeof startLng !== 'number' || !Number.isFinite(startLng) || typeof startLat !== 'number' || !Number.isFinite(startLat) || typeof endLng !== 'number' || !Number.isFinite(endLng) || typeof endLat !== 'number' || !Number.isFinite(endLat)) {
    return EMPTY_ROUTE;
  }
  const startPoint = map.project([startLng, startLat]);
  const endPoint = map.project([endLng, endLat]);
  const deltaX = endPoint.x - startPoint.x;
  const deltaY = endPoint.y - startPoint.y;
  const distance = Math.hypot(deltaX, deltaY);
  if (distance === 0) return EMPTY_ROUTE;
  const directionX = deltaX / distance;
  const directionY = deltaY / distance;
  const totalPadding = PLAYER_MARKER_PADDING_PX + TARGET_MARKER_PADDING_PX;
  const paddingScale = Math.min(1, Math.max(0, distance - MIN_ROUTE_LENGTH_PX) / totalPadding);
  const insetStart = map.unproject([startPoint.x + directionX * PLAYER_MARKER_PADDING_PX * paddingScale, startPoint.y + directionY * PLAYER_MARKER_PADDING_PX * paddingScale]);
  const insetEnd = map.unproject([endPoint.x - directionX * TARGET_MARKER_PADDING_PX * paddingScale, endPoint.y - directionY * TARGET_MARKER_PADDING_PX * paddingScale]);
  return {
    type: 'FeatureCollection',
    features: [{
      ...feature,
      geometry: {
        type: 'LineString',
        coordinates: [[insetStart.lng, insetStart.lat], [insetEnd.lng, insetEnd.lat]]
      }
    }]
  };
}
function getRouteDataKey(map: MapLibre.Map, route: GeoJSON.FeatureCollection<GeoJSON.LineString>): string {
  const coordinates = route.features[0]?.geometry.coordinates;
  if (!coordinates) return 'empty';
  return `${JSON.stringify(coordinates)}:${map.getZoom().toFixed(4)}:${map.getBearing().toFixed(2)}:${map.getPitch().toFixed(2)}`;
}
export function CompassRouteLayer() {
  const {
    map
  } = useMapContext();
  const analytics = useAnalytics();
  const hostType = useHostType();
  const mapId = useAppSelector(selectExternalMapId);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const locationEnabled = useAppSelector(selectLocationEnabled);
  const playerCoords = useAppSelector(selectPlayerCoords);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  const trackedCompassPin = useAppSelector(selectTrackedCompassPin);
  const playerX = playerCoords?.x;
  const playerY = playerCoords?.y;
  const playerZ = playerCoords?.z;
  const targetLng = trackedCompassPin?.lngLat.lng;
  const targetLat = trackedCompassPin?.lngLat.lat;
  const coordinateDeltas = playerLocationDeltas?.deltas;
  const route = useMemo<GeoJSON.FeatureCollection<GeoJSON.LineString>>(() => {
    if (!locationEnabled || playerX === undefined || playerY === undefined || playerZ === undefined || !coordinateDeltas || targetLng === undefined || targetLat === undefined) {
      return EMPTY_ROUTE;
    }
    const playerLngLat = gameCoordsToLatLng({
      x: playerX,
      y: playerY,
      z: playerZ
    }, coordinateDeltas);
    if (!playerLngLat) return EMPTY_ROUTE;
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [[playerLngLat.lng, playerLngLat.lat], [targetLng, targetLat]]
        }
      }]
    };
  }, [locationEnabled, playerX, playerY, playerZ, coordinateDeltas, targetLng, targetLat]);
  const routeRef = useRef(route);
  const impressionKeyRef = useRef<string | null>(null);
  const reportRouteImpressionRef = useRef<(targetMap: MapLibre.Map) => void>(() => undefined);
  const updateRouteRef = useRef<() => void>(() => undefined);
  reportRouteImpressionRef.current = targetMap => {
    if (!trackedCompassPin) return;
    const key = `${mapId ?? ''}:${trackedCompassPin.externalId}`;
    if (impressionKeyRef.current === key) return;
    if (insetRouteEndpoints(targetMap, route).features.length === 0) return;
    if (!targetMap.getSource(SOURCE_ID) || !targetMap.getLayer(LINE_LAYER_ID)) return;
    impressionKeyRef.current = key;
    analytics.event('game_map_compass_route_impression', {
      mapId,
      locationId: trackedCompassPin.externalId,
      platform: hostType,
      isAuthenticated
    });
  };
  useEffect(() => {
    if (!trackedCompassPin) impressionKeyRef.current = null;
  }, [trackedCompassPin]);
  useEffect(() => {
    if (!map) return;
    const isMapRemoved = () => (map as MapLibre.Map & {
      _removed?: boolean;
    })._removed === true;
    let appliedRouteDataKey: string | null = null;
    const syncRouteData = () => {
      if (isMapRemoved() || !map.isStyleLoaded()) return;
      const source = map.getSource(SOURCE_ID) as MapLibre.GeoJSONSource | undefined;
      if (!source) return;
      const key_0 = getRouteDataKey(map, routeRef.current);
      if (key_0 === appliedRouteDataKey) return;
      appliedRouteDataKey = key_0;
      source.setData(insetRouteEndpoints(map, routeRef.current));
      reportRouteImpressionRef.current(map);
    };
    const initialize = () => {
      if (isMapRemoved() || !map.isStyleLoaded()) return;
      const firstPinLayerId = PIN_INTERACTION_LAYER_IDS.find(layerId => map.getLayer(layerId));
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: EMPTY_ROUTE
        });
        appliedRouteDataKey = null;
      }
      if (!map.getLayer(GLOW_LAYER_ID)) {
        map.addLayer({
          id: GLOW_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: {
            'line-color': '#d9ff00',
            'line-width': 10,
            'line-opacity': 0.18,
            'line-blur': 4
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          }
        }, firstPinLayerId);
      }
      if (!map.getLayer(LINE_LAYER_ID)) {
        map.addLayer({
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: {
            'line-color': '#d9ff00',
            'line-width': 3,
            'line-opacity': 0.9,
            'line-dasharray': [1, 1.5]
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          }
        }, firstPinLayerId);
      }
      if (firstPinLayerId) {
        const moveBelowPins = (layerId_0: string) => {
          const layers = map.getStyle().layers ?? [];
          const layerIndex = layers.findIndex(layer => layer.id === layerId_0);
          const pinIndex = layers.findIndex(layer_0 => layer_0.id === firstPinLayerId);
          if (layerIndex > pinIndex) map.moveLayer(layerId_0, firstPinLayerId);
        };
        moveBelowPins(GLOW_LAYER_ID);
        moveBelowPins(LINE_LAYER_ID);
      }
      syncRouteData();
      reportRouteImpressionRef.current(map);
    };
    updateRouteRef.current = () => {
      if (map.getSource(SOURCE_ID) && map.getLayer(GLOW_LAYER_ID) && map.getLayer(LINE_LAYER_ID)) {
        syncRouteData();
        return;
      }
      initialize();
    };
    if (map.isStyleLoaded()) initialize();
    map.on('load', initialize);
    map.on('styledata', initialize);
    map.on('idle', initialize);
    map.on('move', syncRouteData);
    return () => {
      updateRouteRef.current = () => undefined;
      map.off('load', initialize);
      map.off('styledata', initialize);
      map.off('idle', initialize);
      map.off('move', syncRouteData);
      if (isMapRemoved()) return;
      if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
      if (map.getLayer(GLOW_LAYER_ID)) map.removeLayer(GLOW_LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  }, [map]);
  useEffect(() => {
    if ((map as (MapLibre.Map & {
      _removed?: boolean;
    }) | null)?._removed) return;
    routeRef.current = route;
    updateRouteRef.current();
  }, [map, route]);
  return null;
}